import { TFunction } from "i18next";
import { Waybill } from "Models/waybill";
import { CompanyProfile, ShipmentType } from "Util/enums";
import * as yup from "yup";

import { buildAirPublishSchema, buildSeaPublishSchema } from "./waybillSchemas";

export { buildAirPublishSchema, buildSeaPublishSchema } from "./waybillSchemas";

export type ValidateWaybillForPublishResult =
  | { ok: true }
  | {
      ok: false;
      errors: yup.ValidationError[];
      reason?: "no_jobs_or_customers";
    };

export type ValidateWaybillForPublishOptions = {
  companyProfileInitials?: string;
};

export function validateWaybillForPublish(
  waybill: Waybill,
  translate: TFunction,
  options: ValidateWaybillForPublishOptions = {}
): ValidateWaybillForPublishResult {
  const isMvCompany = options.companyProfileInitials === CompanyProfile.MV;

  if (
    !waybill?.jobs?.length &&
    !waybill?.customers?.length &&
    !waybill?.customersToShipment?.length
  ) {
    return { ok: false, errors: [], reason: "no_jobs_or_customers" };
  }

  const isAir = waybill?.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase();

  if (isAir) {
    const airValidationPayload = {
      isSubscribed: true,
      commodity: waybill?.commodity,
      containerId: waybill?.containerId,
      shipmentType: waybill?.shipmentType,
      jobs: waybill?.jobs,
      customers: waybill?.customers,
    };

    try {
      buildAirPublishSchema(translate, isMvCompany).validateSync(airValidationPayload, { abortEarly: false });
    } catch (error: unknown) {
      if (error instanceof yup.ValidationError) {
        const errors = error.inner?.length ? error.inner : [error];
        return { ok: false, errors };
      }
      return { ok: false, errors: [] };
    }

    return { ok: true };
  }

  const seaValidationPayload = {
    isSubscribed: true,
    containerId: waybill?.containerId,
    shipmentType: waybill?.shipmentType,
    loadType: waybill?.loadType,
    billOfLanding: waybill?.billOfLanding,
    jobs: waybill?.jobs,
    containers: waybill?.containers,
    customers: waybill?.customers || waybill?.customersToShipment,
  };

  try {
    buildSeaPublishSchema(translate).validateSync(seaValidationPayload, { abortEarly: false });
  } catch (error: unknown) {
    if (error instanceof yup.ValidationError) {
      const errors = error.inner?.length ? error.inner : [error];
      return { ok: false, errors };
    }
    return { ok: false, errors: [] };
  }

  return { ok: true };
}
